import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCTA from "@/components/site/MobileCTA";
import ScrollScanner from "@/components/ScrollScanner";
import Reveal from "@/components/site/Reveal";
import RelatedLinks from "@/components/site/RelatedLinks";
import RelatedGuides from "@/components/site/RelatedGuides";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { trackPhoneCallLead } from "@/lib/ads";
import { useSeo } from "@/lib/useSeo";
import { useJsonLd } from "@/lib/useJsonLd";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import { base44 } from "@/api/base44Client";

const areaSlug = (a) => a.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const buildAreaFaqs = (area) => [
  {
    q: `How quickly can Meridian respond to bed bugs in ${area.name}?`,
    a: `We aim to schedule treatments across ${area.name}${area.base_city ? ` and the wider ${area.base_city} area` : ""} as quickly as possible. Availability depends on demand and property size — share your details through the booking form and we'll confirm the earliest appointment.`
  },
  {
    q: `Can bed bugs spread between units in ${area.name}?`,
    a: `Yes. In multi-unit buildings such as condos and apartments, bed bugs can move between connected suites through shared walls and electrical pathways. In ${area.name}, early action and — where relevant — coordination across units matter, because one unit's report doesn't always mean a single-unit problem.`
  },
  {
    q: `Do I need to throw out my mattress if I have bed bugs in ${area.name}?`,
    a: `Not usually. Mattresses are commonly treated rather than discarded. We assess the level of activity and the mattress condition during your ${area.name} treatment and advise accordingly.`
  },
  {
    q: `Is your bed bug service discreet in ${area.name}?`,
    a: `Yes. Our team works with discretion in ${area.name} — unmarked service, respectful communication and professional application — so neighbours, guests and other residents aren't drawn into the situation.`
  }
];

export default function ServiceAreaPage() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: area ? `Bed Bug Control & Treatment in ${area.name}, BC | Meridian` : "Bed Bug Treatment | Meridian",
    description: area?.description || "Specialized bed bug treatment across Vancouver and the Lower Mainland — signs, preparation, treatment and follow-up.",
    path: `/bed-bug-treatment/${slug}`,
    type: "article",
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setArea(null);
    base44.entities.ServiceArea.filter({ slug })
      .then((rows) => { if (active) { setArea(rows?.[0] || null); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  /* page title, meta, canonical and social tags set by useSeo */

  useJsonLd("area-jsonld", area ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Bed Bug Treatment",
    "provider": {
      "@type": "PestControl",
      "name": "Meridian Bed Bug Treatment",
      "telephone": BRAND.phoneTel,
      "areaServed": [area.name],
      "address": { "@type": "PostalAddress", "addressLocality": "Vancouver", "addressRegion": "BC", "addressCountry": "CA" }
    },
    "areaServed": [area.name],
    "url": `https://meridianbedbug.ca/bed-bug-treatment/${slug}`
  } : {});

  const faqs = area ? buildAreaFaqs(area) : [];
  useJsonLd("area-faq-jsonld", faqs.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : {});

  if (loading) {
    return (
      <div className="overflow-x-hidden pb-16 lg:pb-0">
        <Navbar />
        <div className="mx-auto max-w-3xl px-5 py-40">
          <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
          <div className="mt-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="overflow-x-hidden pb-16 lg:pb-0">
        <Navbar />
        <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-5 text-center">
          <h1 className="font-heading text-3xl font-bold text-primary">Service area not found</h1>
          <Button asChild className="mt-6 h-11 bg-accent text-white hover:bg-accent/90"><Link to="/">Back to home</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const neighborhoods = area.neighborhoods ? area.neighborhoods.split(",").map((n) => n.trim()).filter(Boolean) : [];

  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <ScrollScanner />
      <Navbar />
      <main id="main-content">
        <section className="bg-background px-5 pt-32 pb-8 lg:px-8 lg:pt-40">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Bed Bug Treatment", to: "/bed-bug-treatment-vancouver" }, { name: `Bed Bug Treatment in ${area.name}` }]} className="mb-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Service Area</span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-primary lg:text-4xl">
                Bed Bug Control & Treatment in {area.name}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">{area.description}</p>
            </Reveal>
          </div>
        </section>

        <section className="bg-background px-5 pt-12 pb-24 lg:px-8 lg:pt-16 lg:pb-32">
          <div className="mx-auto max-w-3xl space-y-8 text-base leading-relaxed text-muted-foreground">
            <Reveal>
              <div className="max-w-none space-y-6">
                <p>
                  Meridian provides specialized bed bug treatment for homes, condos, apartments, hotels and short-term rentals in {area.name}{area.base_city ? ` and the wider ${area.base_city} area` : ""}. Because bed bugs hide deep in mattress seams, bed frame joints and baseboard gaps — and travel easily between connected units — a general pest spray is rarely enough. Our team follows a disciplined, specialist process: Book → Prepare → Treat → Aftercare → Follow-up.
                </p>
                <h2 className="font-heading text-xl font-bold text-primary">Signs of bed bugs in {area.name}</h2>
                <p>
                  Watch for itchy bites that appear in lines or clusters after sleeping, dark rust-coloured spots on sheets and mattress seams, shed translucent skins, and a faint sweet musty smell in heavily infested rooms. In multi-unit buildings across {neighborhoods.length ? `areas such as ${neighborhoods.slice(0, 4).join(", ")}` : area.name}, bed bugs can spread between suites through shared walls and electrical pathways, so early action matters.
                </p>
                <h2 className="font-heading text-xl font-bold text-primary">How treatment works</h2>
                <p>
                  After booking, you receive clear preparation instructions — stripping and laundering bedding, reducing clutter near sleeping areas, and moving furniture away from walls where instructed. Treatment targets the places bed bugs actually hide: mattress and box spring seams, bed frame joints, baseboard gaps, headboards and surrounding furniture. People and pets leave during treatment and return once the product has dried and the area is safe, following aftercare guidance.
                </p>
                <h2 className="font-heading text-xl font-bold text-primary">Prevention and follow-up</h2>
                <p>
                  Prevention comes down to awareness after travel, careful inspection of second-hand furniture, and quick response at the first sign of activity. Where the treatment program calls for it, a follow-up visit targets any newly hatched insects missed by the first pass. Every job in {area.name} is handled with discretion so neighbours, guests and other residents are not drawn into the situation.
                </p>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-lg border border-border bg-muted/30 p-6">
                <h3 className="font-heading text-lg font-bold text-primary">Ready to book a treatment in {area.name}?</h3>
                <p className="mt-2 text-sm text-muted-foreground">Call us or use the booking form on the home page to share a few details about your property.</p>
                <a href={`tel:${BRAND.phoneTel}`} onClick={trackPhoneCallLead} className="mt-4 inline-flex items-center gap-2 font-semibold text-accent">{BRAND.phoneDisplay}</a>
              </div>
            </Reveal>
            {neighborhoods.length > 0 && (
              <Reveal>
                <div>
                  <h3 className="font-heading text-base font-bold text-primary">{area.name} neighbourhoods we treat</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {neighborhoods.map((n) => (
                      <Link key={n} to={`/bed-bug-treatment/${slug}/${areaSlug(n)}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{n}</Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
            <Reveal>
              <div>
                <h3 className="font-heading text-base font-bold text-primary">More service areas</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BRAND.areas.filter((a) => areaSlug(a) !== slug).map((a) => (
                    <Link key={a} to={a === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${areaSlug(a)}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{a}</Link>
                  ))}
                </div>
                <Link to="/bed-bug-treatment-areas" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">View all service areas <ArrowRight className="h-4 w-4" /></Link>
                <h3 className="mt-8 font-heading text-base font-bold text-primary">Treatment by property type</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/bed-bug-treatment/condos" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Condos</Link>
                  <Link to="/bed-bug-treatment/apartments" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Apartments</Link>
                  <Link to="/bed-bug-treatment/hotels" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Hotels</Link>
                  <Link to="/bed-bug-treatment/short-term-rentals" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Airbnb & Short-Term Rentals</Link>
                  <Link to="/bed-bug-treatment/homes" className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Homes</Link>
                </div>
              </div>
            </Reveal>
            <RelatedGuides match={area.name} />
            <Reveal>
              <div>
                <h3 className="font-heading text-lg font-bold text-primary">{area.name} bed bug treatment — common questions</h3>
                <div className="mt-4 divide-y divide-border border-y border-border">
                  {faqs.map((f) => (
                    <div key={f.q} className="py-4">
                      <p className="font-heading text-sm font-semibold text-primary">{f.q}</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        <RelatedLinks links={[
          { to: "/guide", label: "Bed Bug Guide", title: "Browse the full Bed Bug Guide", desc: "Practical articles on signs, preparation, treatment and prevention — written for people dealing with the problem.", cta: "Read the guide" },
          { to: "/#booking", label: "Book", title: "Book a treatment", desc: "Send a few details about your property and we'll confirm availability and treatment requirements.", cta: "Book treatment" },
          { to: "/bed-bug-treatment-vancouver", label: "Vancouver", title: "Bed Bug Treatment in Vancouver", desc: "Our comprehensive Vancouver treatment guide — signs, preparation and what to expect.", cta: "Read more" },
        ]} />
        <div className="px-5 pb-24 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-accent">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}